import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { MapPin, Calendar, Clock, Users, DollarSign, CheckCircle, AlertTriangle, Eye } from 'lucide-react';
import { formatDate } from '../utils/timeFormat';
import ClientReputationBadge from './ClientReputationBadge';  // Iter-225r

/**
 * GigPreviewModal - Shows exactly how a gig will appear to talent
 * Used in CreateShift page for clients to preview before publishing
 */
const GigPreviewModal = ({ 
  open, 
  onClose,
  // Gig data
  client_id,  // Iter-225r — used to fetch Talent → Client reputation
  role_name,
  venue_name,
  venue_address,
  city,
  state,
  shift_date,
  start_time,
  end_time,
  hourly_rate,
  talent_needed,
  tier_required,
  // Enhanced gig details
  duties,
  dress_code_name,
  dress_code_checklist = [],
  dress_code_description,
  dress_code_images = [],
  attire_restrictions = [],
  grooming_requirements = [],
  grooming_images = [],
  send_home_warning,
  additional_notes,
}) => {
  // Format time to 12-hour format
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':').map(Number);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;
    return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const formatTimeRange = (start, end) => {
    return `${formatTime(start)} - ${formatTime(end)}`;
  };

  // Format date — Iter-225b: delegate to shared util.
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Eye className="w-5 h-5 text-cyan-500" />
            <span className="text-sm font-medium text-cyan-600">Gig Preview - Talent View</span>
          </div>
          <DialogTitle className="text-2xl" data-testid="preview-role-name">{role_name || 'Role'}</DialogTitle>
          <DialogDescription>
            {venue_name || 'Venue'} • {city}, {state}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Iter-225r — Talent → Client reputation panel, ABOVE pay/spots
              so talent see the trust signal before committing. */}
          {client_id && (
            <ClientReputationBadge clientId={client_id} variant="detail" />
          )}

          {/* 1. Role + 2. Pay Rate */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-100">
              <div className="text-sm text-muted-foreground mb-1">Pay Rate</div>
              <div className="font-bold text-cyan-600 text-xl" data-testid="preview-pay-rate">
                ${hourly_rate || '0'}/hr
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Spots Available</div>
              <div className="font-medium" data-testid="preview-spots">{talent_needed || 1}</div>
            </div>
          </div>

          {/* 3. Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Date</div>
              <div className="font-medium" data-testid="preview-date">
                {shift_date ? formatDate(shift_date) : 'Not set'}
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Shift Time</div>
              <div className="font-medium" data-testid="preview-time">
                {start_time && end_time ? formatTimeRange(start_time, end_time) : 'Not set'}
              </div>
            </div>
          </div>

          {/* 4. Location */}
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="text-sm text-muted-foreground mb-1">Location</div>
            <div className="font-medium" data-testid="preview-venue">{venue_name || 'Venue name not set'}</div>
            {venue_address && <div className="text-sm text-gray-500">{venue_address}</div>}
            <div className="text-sm text-gray-500">{city}, {state}</div>
          </div>

          {/* 5. Duties & Responsibilities */}
          {duties && (
            <div data-testid="preview-duties">
              <h4 className="font-semibold mb-2 text-slate-900">Duties & Responsibilities</h4>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{duties}</p>
            </div>
          )}

          {/* 6. Dress Code */}
          {(dress_code_name || dress_code_checklist?.length > 0) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="preview-dress-code">
              <h4 className="font-semibold mb-2 text-blue-900 flex items-center gap-2">
                Dress Code{dress_code_name ? `: ${dress_code_name}` : ''}
              </h4>
              {dress_code_checklist?.length > 0 && (
                <ul className="space-y-1 mb-2">
                  {dress_code_checklist.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-blue-800">
                      <CheckCircle className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
              {dress_code_description && (
                <p className="text-sm text-blue-700">{dress_code_description}</p>
              )}
              {dress_code_images?.length > 0 && (
                <div className="flex gap-2 mt-3">
                  {dress_code_images.map((url, i) => (
                    <img key={i} src={url} alt="Dress code reference" className="w-24 h-24 rounded-lg object-cover border border-blue-200" />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 6b. Attire Restrictions */}
          {attire_restrictions?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="preview-attire-restrictions">
              <h4 className="font-semibold mb-2 text-red-900">Attire Restrictions</h4>
              <div className="grid grid-cols-2 gap-1">
                {attire_restrictions.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-red-700">
                    <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />
                    {r}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 7. Grooming Requirements */}
          {grooming_requirements?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="preview-grooming">
              <h4 className="font-semibold mb-2 text-amber-900">Grooming Requirements</h4>
              <div className="grid grid-cols-1 gap-1">
                {grooming_requirements.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-amber-800">
                    <CheckCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    {r}
                  </div>
                ))}
              </div>
              {grooming_images?.length > 0 && (
                <div className="flex gap-2 mt-3">
                  {grooming_images.map((url, i) => (
                    <img key={i} src={url} alt="Grooming reference" className="w-24 h-24 rounded-lg object-cover border border-amber-200" />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Send Home Warning */}
          {send_home_warning && (
            <div className="bg-red-100 border border-red-300 rounded-lg p-3 flex items-center gap-2" data-testid="preview-send-home-warning">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
              <p className="text-sm font-medium text-red-800">This partner may send you home if these requirements are not met.</p>
            </div>
          )}

          {/* 8. Additional Notes */}
          {additional_notes && (
            <div data-testid="preview-additional-notes">
              <h4 className="font-semibold mb-2 text-slate-900">Additional Notes & Instructions</h4>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{additional_notes}</p>
            </div>
          )}

          {/* Tier Badge */}
          {tier_required === 'premium' && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <Badge className="bg-purple-100 text-purple-800">Premium Only</Badge>
                <span className="text-sm text-purple-900">This shift requires Premium tier membership</span>
              </div>
            </div>
          )}

          {/* Preview Notice */}
          <div className="bg-gray-100 rounded-lg p-4 border border-gray-200 flex items-start gap-3">
            <Eye className="w-5 h-5 text-gray-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-gray-700 font-medium">This is how talent will see your gig posting</p>
              <p className="text-xs text-gray-500 mt-1">
                Make sure all information is accurate before publishing. Talent will use this to decide whether to apply.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="close-preview-btn">
            Close Preview
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GigPreviewModal;
